import { Collection, CollectionAccessEnum, CollectionItem, IBillingCard } from 'lingo2-models';

export type ValidationError = 'empty' | 'min_length' | 'max_length' | 'content_incomplete' | 'nocards';

export interface IValidationMessages<T> {
  field: keyof T;
  error: ValidationError;
  message?: string;
  value?: any;
}

export class CollectionValidator {
  constructor(public collection?: Collection, public items?: CollectionItem[], public cards?: IBillingCard[]) {}

  public get isValid(): boolean {
    return Object.values(this.warnings).length === 0;
  }

  public get warnings(): Array<IValidationMessages<Collection>> {
    switch (this.collection.access) {
      case CollectionAccessEnum.private:
        return this.validateAsPrivate();

      case CollectionAccessEnum.commercial:
        return this.validateAsCommercial();

      default:
        const _access = this.collection.access
          ? CollectionAccessEnum[this.collection.access] || 'unknown'
          : 'undefined';
        throw new Error(`Can't validate collections with access '${_access}'`);
    }
  }

  protected validateAsPrivate(): Array<IValidationMessages<Collection>> {
    const messages: Array<IValidationMessages<Collection>> = [];

    const { title } = this.collection;
    if ((title || '').length === 0) {
      messages.push({
        field: 'title',
        error: 'empty',
        message: 'Title not set',
      });
    }
    if ((this.items || []).length === 0) {
      messages.push({
        field: 'items',
        error: 'empty',
        message: 'Not enough content items in collection',
      });
    } else {
      const validItems = this.items.filter((_v) => _v.content && _v.content.valid);
      if (validItems.length === 0) {
        messages.push({
          field: 'items',
          error: 'empty',
          message: 'Not enough content items in collection',
        });
      }
    }

    if (this.cards.length === 0) {
      messages.push({
        field: 'items',
        error: 'empty',
        message: 'Not enough content items in collection',
      });
    }

    return messages;
  }

  protected validateAsCommercial(): Array<IValidationMessages<Collection>> {
    const messages: Array<IValidationMessages<Collection>> = [];

    const {
      title,
      excerpt,
      description,
      // keywords,
      price_tiers,
      // options,
    } = this.collection;
    if ((title || '').length === 0) {
      messages.push({
        field: 'title',
        error: 'empty',
        message: 'Title not set',
      });
    }
    if ((excerpt || '').length === 0) {
      messages.push({
        field: 'excerpt',
        error: 'empty',
        message: 'Excerpt not set',
      });
    }
    if ((description || '').length < 50) {
      messages.push({
        field: 'description',
        error: 'min_length',
        message: 'Description not set',
      });
    }
    if ((this.items || []).length === 0) {
      messages.push({
        field: 'items',
        error: 'empty',
        message: 'Not enough content items in collection',
      });
    } else {
      const inValidItems = this.items.filter((_v) => !(_v.content && _v.content.valid));
      if (inValidItems.length > 0) {
        messages.push({
          field: 'items',
          error: 'content_incomplete',
          message: 'Some content items not valid',
        });
      }
    }
    const activatedLicenses = Object.keys(price_tiers).filter((license) => price_tiers[license] > 0);
    if (activatedLicenses.length === 0) {
      messages.push({
        field: 'items',
        error: 'empty',
        message: 'License not set',
      });
    }

    return messages;
  }
}
